/**
 * Frontend Term Utilities for Multi-Country Support
 * Handles term display and formatting for Ghana (string) and Liberia (object) formats
 */

/**
 * Get term display name
 * @param {string|object} term - The term to display
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string} - Display string
 */
export const getTermDisplay = (term, country = 'GH') => {
    if (!term) return 'No term';
    
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        if (typeof term === 'object' && term.name) {
            return term.name;
        }
        return typeof term === 'string' ? term : 'Invalid term';
    }
    
    return String(term);
};

/**
 * Get term value for form submission or comparison
 * @param {string|object} term - The term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string|object} - Term value
 */
export const getTermValue = (term, country = 'GH') => {
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        if (typeof term === 'object') {
            return term; // Return full object
        }
        // Fallback: create object from string (not recommended)
        return { 
            name: term, 
            startMonth: 1, 
            startYear: new Date().getFullYear(), 
            endMonth: 12, 
            endYear: new Date().getFullYear() 
        };
    }
    return term;
};

/**
 * Get term key for object property access (always returns string)
 * This is used for attendance storage where we need consistent string keys
 * @param {string|object} term - The term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string} - Term key as string
 */
export const getTermKey = (term, country = 'GH') => {
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        // For Liberia, use the term name as the key for attendance storage
        if (typeof term === 'object' && term.name) {
            return term.name;
        }
        return typeof term === 'string' ? term : String(term);
    }
    return String(term);
};

/**
 * Format term with date range for Liberia
 * @param {object} term - Term object (Liberia format)
 * @returns {string} - Formatted string with dates
 */
export const formatTermWithDates = (term) => {
    if (typeof term !== 'object' || !term.name) {
        return getTermDisplay(term);
    }
    
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonth = monthNames[term.startMonth - 1] || term.startMonth;
    const endMonth = monthNames[term.endMonth - 1] || term.endMonth;
    
    return `${term.name} (${startMonth} ${term.startYear} - ${endMonth} ${term.endYear})`;
};

/**
 * Check if term matches (for filtering/comparison)
 * @param {string|object} term1 - First term
 * @param {string|object} term2 - Second term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {boolean} - True if terms match
 */
export const termsMatch = (term1, term2, country = 'GH') => {
    if (country === 'GH') {
        const val1 = typeof term1 === 'string' ? term1 : term1?.name;
        const val2 = typeof term2 === 'string' ? term2 : term2?.name;
        return val1 === val2;
    } else if (country === 'LR') {
        if (typeof term1 === 'object' && typeof term2 === 'object') {
            return term1.name === term2.name;
        }
        return false;
    }
    return false;
};

/**
 * Normalize terms array for display
 * @param {Array} terms - Array of terms
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {Array} - Array of display strings
 */
export const normalizeTermsForDisplay = (terms, country = 'GH') => {
    if (!Array.isArray(terms)) return [];
    
    return terms.map(term => getTermDisplay(term, country));
};

/**
 * Get term options for dropdown (MenuItem format)
 * @param {Array} terms - Array of terms
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {Array} - Array of { value, label } objects
 */
export const getTermOptions = (terms, country = 'GH') => {
    if (!Array.isArray(terms)) return [];
    
    if (country === 'GH') {
        return terms.map(term => {
            const value = typeof term === 'string' ? term : term?.name;
            return {
                value: value,
                label: value
            };
        });
    } else if (country === 'LR') {
        return terms.map(term => {
            if (typeof term === 'object' && term.name) {
                return {
                    value: term.name,
                    label: formatTermWithDates(term),
                    term: term // Include full object for reference
                };
            }
            return {
                value: term,
                label: term
            };
        });
    }
    
    return [];
};

/**
 * Find term by name or value
 * @param {Array} terms - Array of terms
 * @param {string|object} searchTerm - Term to find
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string|object|null} - Found term or null
 */
export const findTerm = (terms, searchTerm, country = 'GH') => {
    if (!Array.isArray(terms)) return null;
    
    if (country === 'GH') {
        const searchValue = typeof searchTerm === 'string' ? searchTerm : searchTerm?.name;
        return terms.find(term => {
            const termValue = typeof term === 'string' ? term : term?.name;
            return termValue === searchValue;
        }) || null;
    } else if (country === 'LR') {
        const searchName = typeof searchTerm === 'object' ? searchTerm.name : searchTerm;
        return terms.find(term => {
            if (typeof term === 'object' && term.name) {
                return term.name === searchName;
            }
            return false;
        }) || null;
    }
    
    return null;
};




